import { Link } from "react-router-dom";
import Logo from "../components/Logo";

export default function PrivacyPolicy() {
  return (
    <div className="min-h-screen" style={{ backgroundColor: "#faf8f3" }}>
      <nav className="sticky top-0 z-50 backdrop-blur-xl bg-[#faf8f3]/80 border-b border-black/[0.06]">
        <div className="max-w-7xl mx-auto px-5 md:px-12 flex items-center justify-between h-16">
          <Logo />
          <div className="flex items-center gap-6">
            <Link to="/browse" className="text-sm font-medium text-[#0d0d0d] hover:text-[#1a6b3c]">Browse</Link>
            <Link to="/contact" className="text-sm font-medium text-[#0d0d0d] hover:text-[#1a6b3c]">Contact</Link>
          </div>
        </div>
      </nav>

      <div className="max-w-3xl mx-auto px-5 md:px-12 py-16">
        <p className="text-xs font-semibold text-[#1a6b3c] uppercase tracking-wider mb-3">Legal</p>
        <h1 className="text-3xl sm:text-4xl font-black text-[#0d0d0d] font-['Fraunces'] mb-2" data-testid="privacy-heading">Privacy Policy</h1>
        <p className="text-sm text-[#888] mb-10">Last updated: April 23, 2026</p>

        <div className="prose-container space-y-8 text-[#555] text-[15px] leading-relaxed">
          <section>
            <h2 className="text-lg font-black text-[#0d0d0d] font-['Fraunces'] mb-3">1. Introduction</h2>
            <p>Low Key Rents ("Company," "we," "us," or "our") is committed to protecting your privacy. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use our platform at <a href="https://lowkey.rent" className="text-[#1a6b3c] hover:underline">lowkey.rent</a> and related services (the "Service").</p>
            <p className="mt-2">By using the Service, you consent to the data practices described in this Privacy Policy. If you do not agree with this policy, please do not use the Service.</p>
          </section>

          <section>
            <h2 className="text-lg font-black text-[#0d0d0d] font-['Fraunces'] mb-3">2. Information We Collect</h2>
            <h3 className="font-semibold text-[#0d0d0d] mt-3 mb-1">Information You Provide</h3>
            <ul className="list-disc pl-6 mt-2 space-y-1">
              <li><strong>Account Information:</strong> Name, email address, phone number, password (hashed)</li>
              <li><strong>Profile Information:</strong> Bio, military status, preferences, profile photo</li>
              <li><strong>Video Content:</strong> Video introductions (renters) and property tour videos (landlords)</li>
              <li><strong>Listing Information:</strong> Property details, photos, location, pricing, amenities</li>
              <li><strong>Financial Information:</strong> Payment details processed through Stripe (we do not store full credit card numbers)</li>
              <li><strong>Communications:</strong> Messages exchanged through our in-app messaging system</li>
              <li><strong>Background Check Data:</strong> Information provided for and resulting from background check services</li>
              <li><strong>Contact Form Submissions:</strong> Name, email, and message content</li>
            </ul>

            <h3 className="font-semibold text-[#0d0d0d] mt-4 mb-1">Information Collected Automatically</h3>
            <ul className="list-disc pl-6 mt-2 space-y-1">
              <li><strong>Device Information:</strong> Browser type, operating system, device identifiers</li>
              <li><strong>Usage Data:</strong> Pages visited, features used, search queries, time spent on pages</li>
              <li><strong>Location Data:</strong> Approximate location based on IP address; precise location only when you grant permission for map-based search</li>
              <li><strong>Cookies:</strong> Session cookies for authentication and preferences</li>
            </ul>
          </section>

          <section>
            <h2 className="text-lg font-black text-[#0d0d0d] font-['Fraunces'] mb-3">3. How We Use Your Information</h2>
            <p>We use your information to:</p>
            <ul className="list-disc pl-6 mt-2 space-y-1">
              <li>Create, maintain, and secure your account</li>
              <li>Connect renters with landlords and facilitate the rental process</li>
              <li>Process payments and background checks</li>
              <li>Provide AI-powered rental matching recommendations</li>
              <li>Enable in-app messaging between users</li>
              <li>Schedule and manage property showings</li>
              <li>Send transactional emails (account verification, payment confirmations, showing notifications)</li>
              <li>Respond to inquiries and provide customer support</li>
              <li>Improve and personalize the Service</li>
              <li>Detect and prevent fraud, abuse, and security incidents</li>
              <li>Comply with legal obligations</li>
            </ul>
          </section>

          <section>
            <h2 className="text-lg font-black text-[#0d0d0d] font-['Fraunces'] mb-3">4. How We Share Your Information</h2>
            <p>We may share your information in the following circumstances:</p>
            <ul className="list-disc pl-6 mt-2 space-y-1">
              <li><strong>Between Users:</strong> Renter profiles (including video intros and background check status) are shared with landlords when applications are submitted. Landlord profiles and listing details are publicly visible.</li>
              <li><strong>Payment Processors:</strong> Payment information is shared with Stripe to process transactions. Stripe's privacy policy governs their use of this data.</li>
              <li><strong>Background Check Providers:</strong> Information necessary to conduct background checks is shared with our third-party verification partners.</li>
              <li><strong>AI Services:</strong> Anonymized listing and preference data may be processed by AI service providers to generate matching scores.</li>
              <li><strong>Legal Requirements:</strong> We may disclose information when required by law, court order, or government regulation, or when necessary to protect our rights, safety, or property.</li>
              <li><strong>Business Transfers:</strong> In the event of a merger, acquisition, or sale of assets, user information may be transferred to the acquiring entity.</li>
            </ul>
            <p className="mt-2">We do <strong>not</strong> sell your personal information to third parties for marketing purposes.</p>
          </section>

          <section>
            <h2 className="text-lg font-black text-[#0d0d0d] font-['Fraunces'] mb-3">5. Data Storage & Security</h2>
            <p>We implement appropriate technical and organizational measures to protect your personal information, including:</p>
            <ul className="list-disc pl-6 mt-2 space-y-1">
              <li>Encryption of passwords using bcrypt hashing</li>
              <li>Secure HTTPS connections for all data transmission</li>
              <li>Session-based authentication with secure, HTTP-only cookies</li>
              <li>Secure cloud storage for uploaded files and videos</li>
              <li>Regular security assessments and monitoring</li>
            </ul>
            <p className="mt-2">While we strive to protect your information, no method of electronic storage or transmission is 100% secure. We cannot guarantee absolute security.</p>
          </section>

          <section>
            <h2 className="text-lg font-black text-[#0d0d0d] font-['Fraunces'] mb-3">6. Data Retention</h2>
            <p>We retain your personal information for as long as your account is active or as needed to provide the Service. Specifically:</p>
            <ul className="list-disc pl-6 mt-2 space-y-1">
              <li><strong>Account data:</strong> Retained until account deletion is requested</li>
              <li><strong>Messages:</strong> Retained for the duration of the conversation and 12 months after account closure</li>
              <li><strong>Payment records:</strong> Retained for 7 years for tax and legal compliance</li>
              <li><strong>Background check results:</strong> Retained for 2 years from the date of the check</li>
              <li><strong>Video content:</strong> Retained until replaced or account deletion</li>
            </ul>
          </section>

          <section>
            <h2 className="text-lg font-black text-[#0d0d0d] font-['Fraunces'] mb-3">7. Your Rights & Choices</h2>
            <p>Depending on your jurisdiction, you may have the following rights:</p>
            <ul className="list-disc pl-6 mt-2 space-y-1">
              <li><strong>Access:</strong> Request a copy of the personal information we hold about you</li>
              <li><strong>Correction:</strong> Request correction of inaccurate personal information</li>
              <li><strong>Deletion:</strong> Request deletion of your personal information (subject to legal retention requirements)</li>
              <li><strong>Portability:</strong> Request your data in a portable format</li>
              <li><strong>Opt-Out:</strong> Opt out of non-essential communications</li>
            </ul>
            <p className="mt-2">To exercise any of these rights, contact us at <a href="mailto:support@lowkey.rent" className="text-[#1a6b3c] hover:underline">support@lowkey.rent</a>.</p>
          </section>

          <section>
            <h2 className="text-lg font-black text-[#0d0d0d] font-['Fraunces'] mb-3">8. Cookies & Tracking</h2>
            <p>We use essential cookies to maintain your session and authentication state. We do not use third-party advertising cookies. The cookies we use include:</p>
            <ul className="list-disc pl-6 mt-2 space-y-1">
              <li><strong>session_token:</strong> Authentication cookie (HTTP-only, secure) — expires after 7 days</li>
            </ul>
            <p className="mt-2">You can control cookies through your browser settings, but disabling cookies may affect the functionality of the Service.</p>
          </section>

          <section>
            <h2 className="text-lg font-black text-[#0d0d0d] font-['Fraunces'] mb-3">9. Children's Privacy</h2>
            <p>The Service is not intended for individuals under the age of 18. We do not knowingly collect personal information from children under 18. If we become aware that we have collected information from a child under 18, we will take steps to delete such information.</p>
          </section>

          <section>
            <h2 className="text-lg font-black text-[#0d0d0d] font-['Fraunces'] mb-3">10. Virginia Consumer Data Protection Act (VCDPA)</h2>
            <p>If you are a Virginia resident, you have additional rights under the VCDPA, including the right to:</p>
            <ul className="list-disc pl-6 mt-2 space-y-1">
              <li>Confirm whether we are processing your personal data</li>
              <li>Access your personal data</li>
              <li>Correct inaccuracies in your personal data</li>
              <li>Delete your personal data</li>
              <li>Obtain a copy of your data in a portable format</li>
              <li>Opt out of targeted advertising, profiling, or sale of personal data</li>
            </ul>
            <p className="mt-2">We do not sell personal data or use it for targeted advertising. To exercise your VCDPA rights, email <a href="mailto:support@lowkey.rent" className="text-[#1a6b3c] hover:underline">support@lowkey.rent</a>.</p>
          </section>

          <section>
            <h2 className="text-lg font-black text-[#0d0d0d] font-['Fraunces'] mb-3">11. Changes to This Policy</h2>
            <p>We may update this Privacy Policy from time to time. We will notify users of material changes by updating the "Last updated" date and, where appropriate, providing additional notice (such as email notification). Your continued use of the Service after changes constitutes acceptance of the updated policy.</p>
          </section>

          <section>
            <h2 className="text-lg font-black text-[#0d0d0d] font-['Fraunces'] mb-3">12. Contact Us</h2>
            <p>If you have questions or concerns about this Privacy Policy or our data practices, please contact us:</p>
            <div className="mt-2 bg-white rounded-xl border border-black/[0.06] p-4 space-y-1">
              <p><strong>Low Key Rents</strong></p>
              <p>Privacy Inquiries: <a href="mailto:support@lowkey.rent" className="text-[#1a6b3c] hover:underline">support@lowkey.rent</a></p>
              <p>General: <a href="mailto:hello@lowkey.rent" className="text-[#1a6b3c] hover:underline">hello@lowkey.rent</a></p>
            </div>
          </section>
        </div>
      </div>

      <footer className="border-t border-black/[0.06] px-5 md:px-12 py-6">
        <div className="max-w-3xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-3">
          <p className="text-xs text-[#888]">&copy; 2026 Low Key Rents. All rights reserved.</p>
          <div className="flex items-center gap-4">
            <Link to="/terms" className="text-xs text-[#888] hover:text-[#1a6b3c]">Terms of Service</Link>
            <Link to="/contact" className="text-xs text-[#888] hover:text-[#1a6b3c]">Contact</Link>
            <a href="mailto:support@lowkey.rent" className="text-xs text-[#888] hover:text-[#1a6b3c]">support@lowkey.rent</a>
          </div>
        </div>
      </footer>
    </div>
  );
}
